import { useEffect, useRef, useState } from "react";
import { api } from "../lib/providers";

/**
 * usePresence — heartbeat-based "who else is here" presence + simple item-level edit lock.
 *
 * Two modes:
 *   1. Page presence (no itemId): polls /api/locks?resource=<resource> every 15s and returns
 *      a list of OTHER active users. The current user does NOT publish a lock — only views.
 *      => Use this on list pages (Positions/Categories/Customers).
 *
 *   2. Item edit lock (itemId): tries to acquire a lock on (resource, itemId). If another user
 *      holds it, returns { locked: true, holder: {...} } and won't auto-acquire. Otherwise
 *      heartbeats every 30s. Releases on unmount.
 *      => Use this in editor forms.
 */
export default function usePresence({ resource, itemId, enabled = true, edit = false } = {}) {
  const [others, setOthers] = useState([]); // others active in this resource (page mode)
  const [holder, setHolder] = useState(null); // who holds this item lock (edit mode)
  const [acquired, setAcquired] = useState(false);
  const heartbeatTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  // ---- Page-presence (list of others) ----
  useEffect(() => {
    if (!enabled || !resource || itemId) return;
    let mounted = true;
    const fetchList = async () => {
      try {
        const { data } = await api.get(`/locks`, { params: { resource } });
        if (mounted) setOthers(Array.isArray(data) ? data : []);
      } catch {
        // ignore — endpoint may be 404 etc.
      }
    };
    fetchList();
    pollTimerRef.current = setInterval(fetchList, 15000);
    return () => {
      mounted = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [enabled, resource, itemId]);

  // ---- Item edit-lock ----
  useEffect(() => {
    if (!enabled || !edit || !resource || !itemId) return;
    let mounted = true;

    const tryAcquire = async () => {
      try {
        await api.post("/locks/acquire", { resource, item_id: itemId });
        if (mounted) {
          setAcquired(true);
          setHolder(null);
        }
      } catch (err) {
        if (mounted) {
          setAcquired(false);
          const detail = err.response?.data?.detail;
          if (detail && typeof detail === "object" && detail.error === "locked") {
            setHolder({
              user_id: detail.user_id,
              user_name: detail.user_name,
              user_email: detail.user_email,
              expires_at: detail.expires_at,
            });
          }
        }
      }
    };
    tryAcquire();
    heartbeatTimerRef.current = setInterval(tryAcquire, 30000);

    const release = () => {
      try {
        navigator.sendBeacon
          ? navigator.sendBeacon(
              `${process.env.REACT_APP_BACKEND_URL}/api/locks/release`,
              new Blob([JSON.stringify({ resource, item_id: itemId })], { type: "application/json" }),
            )
          : api.post("/locks/release", { resource, item_id: itemId });
      } catch {
        // ignore
      }
    };
    window.addEventListener("beforeunload", release);
    return () => {
      mounted = false;
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      window.removeEventListener("beforeunload", release);
      release();
    };
  }, [enabled, edit, resource, itemId]);

  return {
    others,
    holder,
    acquired,
    isLocked: !!holder,
  };
}
